import React from 'react';
import styled from 'styled-components';
import { useFeatureFlags, Module, MODULE_DISPLAY_INFO } from '../../contexts/FeatureFlagContext';

interface ModuleProtectedRouteProps {
  requiredModules: Module[];
  anyOf?: boolean;
  children: React.ReactNode;
}

/**
 * Route guard that checks if the user has the required module licenses.
 * If not, shows an upsell screen instead of the content.
 */
const ModuleProtectedRoute: React.FC<ModuleProtectedRouteProps> = ({
  requiredModules,
  anyOf = false,
  children,
}) => {
  const { isModuleEnabled, areModulesEnabled, anyModuleEnabled, isLoading } = useFeatureFlags();

  if (isLoading) {
    return <LoadingContainer>Loading...</LoadingContainer>;
  }

  const hasAccess = anyOf 
    ? anyModuleEnabled(...requiredModules)
    : areModulesEnabled(...requiredModules);

  if (!hasAccess) {
    return <ModuleUpsellScreen requiredModules={requiredModules} anyOf={anyOf} />;
  }

  return <>{children}</>;
};

interface ModuleUpsellScreenProps {
  requiredModules: Module[];
  anyOf?: boolean;
}

const ModuleUpsellScreen: React.FC<ModuleUpsellScreenProps> = ({ requiredModules, anyOf }) => {
  const { isModuleEnabled } = useFeatureFlags();
  
  const missingModules = requiredModules.filter(m => !isModuleEnabled(m));
  
  return (
    <UpsellContainer>
      <UpsellCard>
        <UpsellIcon>🔒</UpsellIcon>
        <UpsellTitle>Module Not Licensed</UpsellTitle>
        <UpsellDescription>
          {anyOf ? (
            <>This feature requires one of the following modules:</>
          ) : (
            <>This feature requires the following module{missingModules.length > 1 ? 's' : ''}:</>
          )}
        </UpsellDescription>
        
        <ModuleList>
          {missingModules.map(module => {
            const info = MODULE_DISPLAY_INFO[module];
            return (
              <ModuleCard key={module}>
                <ModuleIcon>{info.icon}</ModuleIcon>
                <ModuleInfo>
                  <ModuleName>{info.name}</ModuleName>
                  <ModuleDescription>{info.description}</ModuleDescription>
                </ModuleInfo>
              </ModuleCard>
            );
          })}
        </ModuleList>
        
        <UpsellCTA>
          <CTAText>
            Upgrade your subscription to unlock this feature
          </CTAText>
          <CTAButton onClick={() => window.open('/contact-sales', '_blank')}>
            Contact Sales
          </CTAButton>
          <LearnMoreLink href="/pricing" target="_blank">
            View Pricing Plans →
          </LearnMoreLink>
        </UpsellCTA>
      </UpsellCard>
    </UpsellContainer>
  );
};

// Styled Components

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  color: #94a3b8;
  font-size: 1.1rem;
`;

const UpsellContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: calc(100vh - 200px);
  padding: 2rem;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
`;

const UpsellCard = styled.div`
  background: rgba(30, 41, 59, 0.95);
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 16px;
  padding: 3rem;
  max-width: 600px;
  width: 100%;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
`;

const UpsellIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1.5rem;
`;

const UpsellTitle = styled.h2`
  color: #f1f5f9;
  font-size: 1.75rem;
  font-weight: 700;
  margin: 0 0 1rem 0;
`;

const UpsellDescription = styled.p`
  color: #94a3b8;
  font-size: 1.1rem;
  margin: 0 0 2rem 0;
  line-height: 1.6;
`;

const ModuleList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const ModuleCard = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.3);
  border-radius: 12px;
  padding: 1rem 1.5rem;
  text-align: left;
`;

const ModuleIcon = styled.div`
  font-size: 2rem;
  flex-shrink: 0;
`;

const ModuleInfo = styled.div`
  flex: 1;
`;

const ModuleName = styled.div`
  color: #f1f5f9;
  font-weight: 600;
  font-size: 1.1rem;
  margin-bottom: 0.25rem;
`;

const ModuleDescription = styled.div`
  color: #94a3b8;
  font-size: 0.9rem;
`;

const UpsellCTA = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  margin-top: 1rem;
  padding-top: 2rem;
  border-top: 1px solid rgba(148, 163, 184, 0.2);
`;

const CTAText = styled.p`
  color: #cbd5e1;
  font-size: 1rem;
  margin: 0;
`;

const CTAButton = styled.button`
  background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.875rem 2rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(59, 130, 246, 0.4);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const LearnMoreLink = styled.a`
  color: #60a5fa;
  font-size: 0.9rem;
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

export default ModuleProtectedRoute;

